import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  refreshIfNeeded,
  startTokenRefreshTimer,
} from '@/lib/auth'
import { destroyDevice, getDevice, relancerTwilio } from '@/lib/twilioDeviceSingleton'
import { destroyPdDevice, getPdDevice } from '@/lib/twilioPdDeviceSingleton'
import { deconnecterTelnyx, relancerTelnyx } from '@/lib/voix/telnyx'
import { canalServeur, ecouterRelais, type MessageRelais, type RelaisOnglet } from '@/lib/relaisOnglet'
import { api } from '@/lib/api'
import { useSSE } from '@/contexts/SSEContext'
import { useProfile } from '@/api/profile'
import { useAvailableCallerIds } from '@/api/agents'
import { readSessionCallerId, saveSessionCallerId } from '@/lib/sessionCallerId'
import { useCallStore } from '@/stores/useCallStore'
import { useTabStateStore, type RaisonInactif } from '@/stores/useTabStateStore'
import { STORAGE_KEYS } from '@/lib/config'

const DEVICE_HEALTH_INTERVAL = 60_000
const OFFLINE_GRACE_MS = 3_000
const TAKEOVER_CHANNEL = 'callizy-takeover'

/** Cet onglet est en appel (sonnerie, en ligne, en attente, annonce) : il garde le téléphone. */
function ongletEnAppel(): boolean {
  const appel = useCallStore.getState()
  return (
    appel.currentCall !== null ||
    appel.isOnHold ||
    appel.retourAttendu ||
    appel.annonce !== null ||
    !!getDevice()?.isBusy ||
    !!getPdDevice()?.isBusy
  )
}

/**
 * Side effects critiques montés une seule fois au-dessus du dashboard.
 *
 * - JWT auto-refresh (timer 45 min) + refreshIfNeeded au mount
 * - Re-validation des données quand le tab redevient visible
 * - Toasts online/offline + invalidate au retour réseau
 * - BroadcastChannel takeover : 1 compte = 1 tab actif (mono-device frontend)
 * - Twilio device health check (60 s) : re-register si state passe en unregistered
 * - preferred_caller_id auto-apply au démarrage de la session
 */
export function useDashboardLifecycle() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const profileQuery = useProfile()
  const { onRelaisTelephone } = useSSE()
  // The context hands out a new function on every render: the takeover effect
  // must not re-claim the phone each time, so it reads it through a ref.
  const onRelaisRef = useRef(onRelaisTelephone)
  onRelaisRef.current = onRelaisTelephone
  const { data: lignes } = useAvailableCallerIds()
  const tabIdRef = useRef<string>(
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2),
  )
  const tabClaimTsRef = useRef<number>(Date.now())

  // 1. JWT auto-refresh
  useEffect(() => {
    void refreshIfNeeded()
    const id = startTokenRefreshTimer()
    return () => clearInterval(id)
  }, [])

  // 2. visibilitychange — refresh data + token quand le tab redevient visible
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState !== 'visible') return
      void refreshIfNeeded()
      queryClient.invalidateQueries({ queryKey: ['call-logs'] })
      queryClient.invalidateQueries({ queryKey: ['sms-conversations'] })
      queryClient.invalidateQueries({ queryKey: ['voicemails'] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [queryClient])

  // 3. online/offline
  // A drop shorter than OFFLINE_GRACE_MS (Wi-Fi handoff, new IP) shows nothing:
  // the user only sees the toasts when the connection was really gone.
  useEffect(() => {
    let wasOffline = false
    let offlineTimer: ReturnType<typeof setTimeout> | undefined
    const onOnline = () => {
      clearTimeout(offlineTimer)
      offlineTimer = undefined
      if (!wasOffline) return
      wasOffline = false
      toast.success(t('toasts.common.connectionRestored'))
      queryClient.invalidateQueries()
    }
    const onOffline = () => {
      if (offlineTimer || wasOffline) return
      offlineTimer = setTimeout(() => {
        offlineTimer = undefined
        if (navigator.onLine) return
        wasOffline = true
        toast.error(t('toasts.common.connectionLost'))
      }, OFFLINE_GRACE_MS)
    }
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      clearTimeout(offlineTimer)
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [queryClient, t])

  // 4. BroadcastChannel takeover — 1 device Twilio = 1 tab actif
  //
  // Quand un autre tab du même user prend le contrôle :
  //   - on détruit nos Devices Twilio (libère le slot, sinon Twilio rejette
  //     les appels du nouveau tab avec un conflit de WebSocket)
  //   - on marque ce tab inactive dans le store
  //   - DashboardLayout affiche un overlay "Onglet repris" avec un bouton
  //     "Reprendre ici" qui reload (nouveau claim avec ts plus récent)
  //
  // Sauf si ce tab est en appel : il refuse et garde le téléphone. Détruire le
  // Device raccrocherait l'appel (disconnectAll). C'est alors le nouveau tab
  // qui libère ses téléphones et affiche « appel en cours dans un autre onglet ».
  //
  // ⚠️ NE PAS wipe l'auth : l'user peut avoir cliqué sur un lien click-to-call
  // qui ouvre un 2e onglet, on ne veut pas le déconnecter dans son CRM.
  //
  // Le même échange passe aussi par le serveur : le téléphone Callizy ouvert
  // dans HubSpot vit dans un cadre que le navigateur isole, le BroadcastChannel
  // ne l'atteint pas. Un message reçu deux fois (canal et serveur) ne change rien.
  useEffect(() => {
    const userId = profileQuery.data?.id
    if (!userId) return

    // Libère les téléphones sans toucher à l'auth : chez Telnyx aussi,
    // deux onglets connectés sonneraient tous les deux.
    const liberer = (raison: RaisonInactif) => {
      destroyDevice()
      destroyPdDevice()
      deconnecterTelnyx()
      useTabStateStore.getState().setInactive(raison)
    }

    const relais: RelaisOnglet = {
      tabId: tabIdRef.current,
      ts: tabClaimTsRef.current,
      userId,
      enAppel: ongletEnAppel,
      ceder: () => liberer('repris'),
      refuse: () => liberer('appel_ailleurs'),
    }

    const arrets: (() => void)[] = []
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(TAKEOVER_CHANNEL)
      arrets.push(ecouterRelais(channel, relais), () => channel.close())
    }
    const serveur = canalServeur(
      (message: MessageRelais) => void api.post('/api/voice/relay/', message).catch(() => {}),
      (rappel) => onRelaisRef.current(rappel),
    )
    arrets.push(ecouterRelais(serveur, relais))

    return () => arrets.forEach((arreter) => arreter())
  }, [profileQuery.data?.id])

  // 5. preferred_caller_id auto-apply (au premier mount, si rien de valable en session).
  // Une ligne restée d'un autre compte n'empêche plus le numéro préféré.
  useEffect(() => {
    const preferred = profileQuery.data?.preferred_caller_id
    if (!preferred || !lignes) return
    if (readSessionCallerId(lignes)) return
    if (lignes.some((l) => l.phone_number === preferred)) saveSessionCallerId(preferred)
  }, [profileQuery.data?.preferred_caller_id, lignes])

  // 6. Phones health check (toutes les 60 s) : un téléphone qui devrait être
  // prêt et ne l'est pas est relancé (Device Twilio absent ou désinscrit,
  // Telnyx abandonné par le SDK ou en attente d'un jeton). Sans effet sur un
  // téléphone prêt ou en train de se reconnecter seul.
  // Désactivé quand le tab est inactif (un autre tab a pris le slot Device) —
  // sinon on relance un register qui re-claim et déclenche une bataille de tabs.
  useEffect(() => {
    const id = setInterval(() => {
      if (!useTabStateStore.getState().isActive) return
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
      if (!accessToken) return
      relancerTwilio()
      relancerTelnyx()
    }, DEVICE_HEALTH_INTERVAL)
    return () => clearInterval(id)
  }, [])
}
